import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Avatar,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import { useQueryClient } from '@tanstack/react-query';
import { useInventoryProducts } from '../hooks/useInventoryProducts';
import { fr } from '../i18n/fr';
import AddInternalProductDialog from './dialogs/AddInternalProductDialog';
import { ProductSelectionItem, ProductSelectionMode } from '../types';
import { generateBarcodeFromValue } from '../../../components/barcodeHelper';
import { formatPrice } from '../utils/formatters';

const ProductImagePreview: React.FC<{ src?: string; alt: string }> = ({ src, alt }) => {
  const initial = alt ? alt.trim().charAt(0).toUpperCase() : '?';
  return (
    <Avatar
      variant="rounded"
      src={src}
      alt={alt}
      sx={{ width: 48, height: 48, fontSize: 18 }}
    >
      {initial || '?'}
    </Avatar>
  );
};

const BarcodePreview: React.FC<{ value?: string }> = ({ value }) => {
  const barcodeSvg = useMemo(() => {
    if (!value || typeof document === 'undefined') {
      return '';
    }
    return generateBarcodeFromValue(value);
  }, [value]);

  if (!value) {
    return <span>-</span>;
  }

  if (!barcodeSvg) {
    return <span>{value}</span>;
  }

  return (
    <Box
      component="span"
      sx={{
        display: 'inline-block',
        minWidth: 140,
        '& svg': {
          maxWidth: '100%',
          height: 'auto',
        },
      }}
      dangerouslySetInnerHTML={{ __html: barcodeSvg }}
    />
  );
};

interface ProductSelectionModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (products: ProductSelectionItem[]) => void;
  selectionMode?: ProductSelectionMode;
  selectedIds?: string[];
}

const DEFAULT_ROWS_PER_PAGE_OPTIONS = [10, 20, 50];

const ProductSelectionModal: React.FC<ProductSelectionModalProps> = ({
  open,
  onClose,
  onConfirm,
  selectionMode = 'single',
  selectedIds = [],
}) => {
  const queryClient = useQueryClient();
  const { data, isPending, isError, refetch } = useInventoryProducts();
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE_OPTIONS[0]);
  const [internalSelectedIds, setInternalSelectedIds] = useState<string[]>(selectedIds);
  const [addDialogOpen, setAddDialogOpen] = useState(false);

  const initializedRef = useRef(false);

  useEffect(() => {
    if (open) {
      if (!initializedRef.current) {
        setSearch('');
        setPage(0);
        setRowsPerPage(DEFAULT_ROWS_PER_PAGE_OPTIONS[0]);
        setInternalSelectedIds(selectedIds);
        initializedRef.current = true;
      }
    } else {
      if (initializedRef.current) {
        initializedRef.current = false;
      }
      setInternalSelectedIds(selectedIds);
    }
  }, [open, selectedIds]);

  const filtered = useMemo(() => {
    if (!data) return [] as ProductSelectionItem[];
    const term = search.trim().toLowerCase();
    if (!term) {
      return data;
    }
    const toSearchable = (value?: string | number | null) => {
      if (typeof value === 'string') {
        return value.toLowerCase();
      }
      if (typeof value === 'number') {
        return String(value).toLowerCase();
      }
      return '';
    };
    return data.filter((product) => {
      const name = toSearchable(product.name);
      const description = toSearchable(product.description);
      const barcode = toSearchable(product.barcode);
      const sku = toSearchable(product.sku);
      return (
        name.includes(term) ||
        (!!description && description.includes(term)) ||
        (!!barcode && barcode.includes(term)) ||
        (!!sku && sku.includes(term))
      );
    });
  }, [data, search]);

  const paginated = useMemo(() => {
    const start = page * rowsPerPage;
    const end = start + rowsPerPage;
    return filtered.slice(start, end);
  }, [filtered, page, rowsPerPage]);

  const toggleSelection = (id: string) => {
    setInternalSelectedIds((prev) => {
      if (selectionMode === 'single') {
        return prev.includes(id) ? [] : [id];
      }
      if (prev.includes(id)) {
        return prev.filter((value) => value !== id);
      }
      return [...prev, id];
    });
  };

  const handleConfirm = () => {
    if (!data) {
      onConfirm([]);
      return;
    }
    const selectedProducts = data.filter((product) =>
      internalSelectedIds.includes(product.id)
    );
    onConfirm(selectedProducts);
  };

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleAddProductSuccess = async () => {
    await queryClient.invalidateQueries({ queryKey: ['inventory', 'products', 'all'] });
    refetch();
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth>
        <DialogTitle>{fr.product}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Stack direction="row" spacing={1} alignItems="center">
              <TextField
                label={fr.search}
                value={search}
                onChange={(event) => {
                  setSearch(event.target.value);
                  setPage(0);
                }}
                fullWidth
              />
              <Tooltip title={fr.refresh ?? 'Rafraîchir'}>
                <span>
                  <IconButton onClick={() => refetch()} disabled={isPending}>
                    <RefreshIcon />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title={fr.addProduct ?? 'Ajouter un produit'}>
                <span>
                  <IconButton onClick={() => setAddDialogOpen(true)}>
                    <AddCircleOutlineIcon />
                  </IconButton>
                </span>
              </Tooltip>
            </Stack>
            {isPending && (
              <Typography variant="body2" color="text.secondary">
                {fr.loading}
              </Typography>
            )}
            {isError && (
              <Typography variant="body2" color="error">
                {fr.error}
              </Typography>
            )}
            {!isPending && !isError && (
              <>
                <TableContainer>
                  <Table size="small" aria-label="product-selection-table">
                    <TableHead>
                      <TableRow>
                        <TableCell padding="checkbox" />
                        <TableCell>Image</TableCell>
                        <TableCell>{fr.description ?? fr.product}</TableCell>
                        <TableCell>SKU</TableCell>
                        <TableCell>Barcode</TableCell>
                        <TableCell align="right">{fr.price ?? 'Prix'}</TableCell>
                        <TableCell>Type</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {paginated.map((product) => {
                        const checked = internalSelectedIds.includes(product.id);
                        return (
                          <TableRow
                            key={product.id}
                            hover
                            selected={checked}
                            onClick={(event) => {
                              const target = event.target as HTMLElement | null;
                              if (
                                target &&
                                (target.closest('input[type="checkbox"]') ||
                                  target.closest('[role="checkbox"]') ||
                                  target.closest('button'))
                              ) {
                                return;
                              }
                              toggleSelection(product.id);
                            }}
                            sx={{ cursor: 'pointer' }}
                          >
                            <TableCell padding="checkbox">
                              <Checkbox
                                color="primary"
                                checked={checked}
                                onChange={() => {
                                  toggleSelection(product.id);
                                }}
                              />
                            </TableCell>
                            <TableCell>
                              <ProductImagePreview
                                src={product.imageUrl}
                                alt={product.name ?? product.description ?? fr.product}
                              />
                            </TableCell>
                            <TableCell>{product.sku || '-'}</TableCell>
                            <TableCell>
                              <BarcodePreview value={product.barcode} />
                            </TableCell>
                            <TableCell align="right">
                              {formatPrice(
                                product.regularPrice ?? product.price
                              ) || '-'}
                            </TableCell>
                            <TableCell>{product.type || '-'}</TableCell>
                          </TableRow>
                        );
                      })}
                      {!paginated.length && (
                        <TableRow>
                          <TableCell colSpan={7}>
                            <Box py={4} textAlign="center">
                              <Typography variant="body2" color="text.secondary">
                                {fr.noResults}
                              </Typography>
                            </Box>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
                <TablePagination
                  component="div"
                  count={filtered.length}
                  page={page}
                  onPageChange={handleChangePage}
                  rowsPerPage={rowsPerPage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  rowsPerPageOptions={DEFAULT_ROWS_PER_PAGE_OPTIONS}
                />
              </>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>{fr.cancel ?? 'Annuler'}</Button>
          <Button
            onClick={() => {
              handleConfirm();
              onClose();
            }}
            variant="contained"
            disabled={!internalSelectedIds.length}
          >
            {fr.validate ?? 'Valider'}
          </Button>
        </DialogActions>
      </Dialog>
      <AddInternalProductDialog
        open={addDialogOpen}
        onClose={() => setAddDialogOpen(false)}
        onCreated={(product) => {
          setAddDialogOpen(false);
          if (product) {
            setInternalSelectedIds([product.id]);
          }
          handleAddProductSuccess();
        }}
      />
    </>
  );
};

export default ProductSelectionModal;
